import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { 
  getCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  getAllCategories 
} from '../lib/pocketbase';
import { getCategoryImageUrl } from '../lib/config';
import CategoryPath from '../components/ui/CategoryPath';
import { Category, CategoryForm } from '../types';
import NestedSelect from '../components/ui/NestedSelect';

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  const [formData, setFormData] = useState<CategoryForm>({
    title: '',
    title_en: '',
    parent_id: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await getCategories(currentPage, perPage);
      setCategories(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      // Ignore auto-cancellation errors
      if (error?.message?.includes('autocancelled')) {
        return;
      }
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  const fetchAllCategories = useCallback(async () => {
    try {
      const data = await getAllCategories();
      setAllCategories(data);
    } catch (error: any) {
      // Ignore auto-cancellation errors
      if (error?.message?.includes('autocancelled')) {
        return;
      }
      console.error('Error fetching all categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchAllCategories();
  }, [fetchCategories, fetchAllCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('title', formData.title);
      formDataObj.append('title_en', formData.title_en);
      if (formData.parent_id) {
        formDataObj.append('parent_id', formData.parent_id);
      }
      if (formData.avatar) {
        formDataObj.append('avatar', formData.avatar);
      }

      if (editingCategory) {
        await updateCategory(editingCategory.id, formDataObj);
      } else {
        await createCategory(formDataObj);
      }

      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      title: category.title,
      title_en: category.title_en,
      parent_id: category.parent_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('آیا از حذف این دسته‌بندی اطمینان دارید؟')) {
      try {
        await deleteCategory(id);
        fetchCategories();
        fetchAllCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_en: '',
      parent_id: ''
    });
  };

  const toggleExpanded = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getChildCategories = (parentId: string) => {
    return allCategories.filter(cat => cat.parent_id === parentId);
  };

  const renderCategoryRow = (category: Category, level = 0) => {
    const hasChildren = getChildCategories(category.id).length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <React.Fragment key={category.id}>
        <tr className="border-b border-gray-200 hover:bg-gray-50">
          <td className="px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center" style={{ paddingRight: `${level * 20}px` }}>
              {hasChildren && (
                <button
                  onClick={() => toggleExpanded(category.id)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
              <div className="flex items-center">
                                 {category.avatar ? (
                   <img
                     src={getCategoryImageUrl(category.id, category.avatar)}
                     alt={category.title}
                     className="w-8 h-8 rounded-lg object-cover ml-3"
                   />
                 ) : (
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center ml-3">
                    <span className="text-primary-600 text-sm font-medium">
                      {category.title.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">{category.title}</div>
                  <div className="text-sm text-gray-500">{category.title_en}</div>
                </div>
              </div>
            </div>
          </td>
          <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-900">
            {category.parent_id ? (
              <CategoryPath
                category={category}
                allCategories={categories}
                showIcons={false}
                maxDepth={3}
              />
            ) : (
              <span className="text-gray-600 font-medium">دسته اصلی</span>
            )}
          </td>
          <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-500">
            {new Date(category.created).toLocaleDateString('fa-IR')}
          </td>
          <td className="px-3 sm:px-6 py-3 sm:py-4 text-right text-sm font-medium">
            <div className="flex items-center space-x-2 space-x-reverse">
              <button
                onClick={() => handleEdit(category)}
                className="text-primary-600 hover:text-primary-900 p-1"
                title="ویرایش"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(category.id)}
                className="text-red-600 hover:text-red-900 p-1"
                title="حذف"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && (
          getChildCategories(category.id).map(child => renderCategoryRow(child, level + 1))
        )}
      </React.Fragment>
    );
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت دسته‌بندی‌ها</h1>
          <p className="text-gray-600">ایجاد و مدیریت دسته‌بندی‌ها</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 ml-2" />
          افزودن دسته‌بندی
        </button>
      </div>

      {/* Categories Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  نام دسته‌بندی
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  دسته‌بندی والد
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاریخ ایجاد
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map(category => renderCategoryRow(category))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50"
              >
                قبلی
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                بعدی
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  نمایش <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> تا{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, categories.length)}
                  </span>{' '}
                  از <span className="font-medium">{categories.length}</span> نتیجه
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-sm shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCategory ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    عنوان (فارسی)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    عنوان (انگلیسی)
                  </label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <NestedSelect
                    label="دسته‌بندی والد"
                    value={formData.parent_id || ''}
                    onChange={(value) => setFormData({ ...formData, parent_id: value })}
                    options={allCategories.filter(cat => cat.id !== editingCategory?.id)}
                    placeholder="بدون والد"
                    helperText="دسته‌بندی والد را انتخاب کنید (اختیاری)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تصویر
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.files?.[0] })}
                    className="input-field"
                    accept="image/*"
                  />
                </div>
                <div className="flex justify-end space-x-3 space-x-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCategory(null);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    انصراف
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'در حال ذخیره...' : (editingCategory ? 'ویرایش' : 'افزودن')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage; 